import { Alert, Box, Button, Container, Snackbar, TextField, Typography } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { Navbar } from "../components/Navbar/Navbar";
import { useEffect, useState } from "react";
import { nameRegex, emailRegex, subjectRegex } from "../utils/Regex";
import { ThemeProvider } from '@emotion/react';
import { CssBaseline } from '@mui/material';
import { formTheme } from "../utils/theme";
import { Footer } from "../components/Footer/Footer";

const Contact = () => {

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "info" | "warning" | "error">();
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [honeypotName, setHoneypotName] = useState("");
    const [captchaAnswer, setCaptchaAnswer] = useState("");
    const [captchaQuestion, setCaptchaQuestion] = useState("");
    const [captchaResult, setCaptchaResult] = useState<number>(0);
    const [isSending, setIsSending] = useState(false);

    const handleSnackbarClose = (event: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }
        setOpenSnackbar(false);
    };
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // Recupera il valore del honeypot
        const formData = new FormData(event.currentTarget);
        const honeypot = formData.get(honeypotName) as string;

        // Se honeypot è valorizzato, blocca l'invio
        if (honeypot) {
            console.log("Send data.");
            return;
        }
        if (!nameRegex.test(name)) {
            setSnackbarMessage("The name is invalid");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true)
            return;
        }
        if (!emailRegex.test(email)) {
            setSnackbarMessage("The email is invalid");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true)
            return;
        }
        if (!subjectRegex.test(subject)) {
            setSnackbarMessage("The subject is invalid");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true)
            return;
        }
        if (message.length < 10) {
            setSnackbarMessage("The message must be at least 10 characters long.");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true);
            return;
        }
        if (message.length > 5000) {
            setSnackbarMessage("The message is too long (max 5000 characters).");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true);
            return;
        }
        // Verifica la risposta del captcha
        if (parseInt(captchaAnswer) !== captchaResult) {
            setSnackbarMessage("Captcha answer is incorrect.");
            setSnackbarSeverity("warning");
            setOpenSnackbar(true);
            generateCaptcha();
            return;
        }
        setIsSending(true);

        try {
            const response = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, subject, message, honeypot }),
            });

            if (!response.ok) {
                throw new Error("The email service rejected the request.");
            }

            setSnackbarSeverity("success");
            setSnackbarMessage("Message sent successfully!");
            setOpenSnackbar(true);
            setName("");
            setEmail("");
            setSubject("");
            setMessage("");
            setCaptchaAnswer("");
            generateCaptcha();
        } catch (error) {
            console.error("Error sending email", error);
            setSnackbarSeverity("error");
            setSnackbarMessage("Error sending message. Please try again.");
            setOpenSnackbar(true);
        } finally {
            setIsSending(false);
        }
    }

    useEffect(() => {
        //Genera una stringa casuale per il nome del campo
        setHoneypotName(`field-${Math.random().toString(36).substring(2, 15)}`);
        generateCaptcha();
    }, []);
    // Funzione per generare una domanda aritmetica casuale
    const generateCaptcha = () => {
        const num1 = Math.floor(Math.random() * 10);
        const num2 = Math.floor(Math.random() * 10);
        const result = num1 + num2;
        setCaptchaQuestion(`What is ${num1} + ${num2}?`);
        setCaptchaResult(result);
    };

    return (
        <>
            <Navbar />
            <Container component="main" maxWidth="sm">
                <Snackbar
                    onClose={handleSnackbarClose}
                    open={openSnackbar}
                    autoHideDuration={3000}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                >
                    <Alert onClose={handleSnackbarClose} severity={snackbarSeverity}>
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="h3">
                        Contact me
                    </Typography>
                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
                        <ThemeProvider theme={formTheme}>
                            <CssBaseline />
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        required
                                        fullWidth
                                        name="name"
                                        id="name"
                                        label="Name"
                                        autoComplete="given-name"
                                        onChange={(e) => setName(e.target.value)}
                                        value={name}
                                        sx={{
                                            backgroundColor: '#1b1b1b',
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="email"
                                        label="Email"
                                        name="email"
                                        autoComplete="family-name"
                                        onChange={(e) => setEmail(e.target.value)}
                                        value={email}
                                        sx={{
                                            backgroundColor: '#1b1b1b',
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        required
                                        fullWidth
                                        id="subject"
                                        label="Subject"
                                        name="subject"
                                        autoComplete="subject"
                                        onChange={(e) => setSubject(e.target.value)}
                                        value={subject}
                                        sx={{
                                            backgroundColor: '#1b1b1b',
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <TextField
                                        required
                                        fullWidth
                                        multiline
                                        rows={8}
                                        id="message"
                                        label="Message"
                                        name="message"
                                        autoComplete="message"
                                        //helperText={!message.match(messageRegex) && "The message is short"}
                                        onChange={(e) => setMessage(e.target.value)}
                                        value={message}
                                        sx={{
                                            backgroundColor: '#1b1b1b',
                                        }}
                                    />
                                </Grid>
                                {/* Captcha Section */}
                                <Grid size={{ xs: 12 }}>
                                    <Typography variant="h6"
                                        sx={{ userSelect: 'none' }}>
                                        {captchaQuestion}
                                    </Typography>
                                    <TextField
                                        required
                                        fullWidth
                                        id="captcha"
                                        label="Answer"
                                        name="captcha"
                                        type="text"
                                        value={captchaAnswer}
                                        onChange={(e) => setCaptchaAnswer(e.target.value)}
                                        sx={{
                                            backgroundColor: '#1b1b1b',
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            {/* Honeypot Field */}
                            <input type="text" name={honeypotName} style={{ display: "none" }} />
                            <Button
                                type="submit"
                                disabled={isSending}
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                            >
                                {isSending ? "Sending..." : "Send"}
                            </Button>
                        </ThemeProvider>
                    </Box>
                </Box>
            </Container>
            <Footer />
        </>
    )
}

export default Contact;
